import { ConvertNull } from "../utils/ConvertNull";
import { enumPubDest } from "../constants";
import { StringToBool } from "../utils/StringToBool.js";
import { QOptimizePref } from "./QOptimizePref";
import { QSortConfig } from "./QSortConfig";
import { QXMLObject } from "./QXMLObject";
import { ConvertNullInt } from "../utils/ConvertNullInt.js";

export class QDocPrefs extends QXMLObject {
  constructor() {
    super();
    this.fPublishOutputDst = enumPubDest.download;
    this.fPublishOutputFolder = null;
    this.fParentDoc = null;
    this.fLastSort = null;
    this.fLastOptimize = null;
    this.fOptOnApplyTemplate = false;
    this.fShowSubChartNum = false;

    // Temp flag for publish
    this.fForceShowSubChartNumBM = false;
  }

  InitDocPrefs(parentDoc) {
    this.fParentDoc = parentDoc;
    this.SetXMLElementName("docprefs");
  }

  ResetDocPrefs() {
    this.fPublishOutputDst = enumPubDest.download;
    this.fPublishOutputFolder = null;
    this.fParentDoc = null;
    this.fLastSort = null;
    this.fLastOptimize = null;
    this.fOptOnApplyTemplate = false;
    this.fShowSubChartNum = false;
    this.fForceShowSubChartNumBM = false;
  }

  SetOptOnApplyTemplate(optimize) {
    if (optimize != this.fOptOnApplyTemplate) {
      this.fOptOnApplyTemplate = optimize;
      this.fParentDoc.SetDirty(true);
    }
  }

  GetOptOnApplyTemplate() {
    return this.fOptOnApplyTemplate;
  }

  SetShowSubChartNumBM(forceShowSCNum) {
    this.fForceShowSubChartNumBM = forceShowSCNum;
  }

  GetShowSubChartNumBM() {
    return this.fForceShowSubChartNumBM;
  }

  SetShowSubChartNum(showSCNum, setDirty) {
    if (showSCNum != this.fShowSubChartNum) {
      this.fShowSubChartNum = showSCNum;
      if (setDirty) this.fParentDoc.SetDirty(true);
    }
  }

  GetShowSubChartNum() {
    return this.fShowSubChartNum;
  }

  SetPubDest(pubDest) {
    if (pubDest != this.fPublishOutputDst) {
      this.fPublishOutputDst = pubDest;
      this.fParentDoc.SetDirty(true);
    }
  }

  SetPubDestFolder(pubDestFolder) {
    if (pubDestFolder != this.fPublishOutputFolder) {
      this.fPublishOutputFolder = pubDestFolder;
      this.fParentDoc.SetDirty(true);
    }
  }

  GetPubDest() {
    return this.fPublishOutputDst;
  }

  GetPubDestFolder() {
    return this.fPublishOutputFolder;
  }

  OutputSubElements(stream) {
    this.OutputElementPair("pubdest", this.fPublishOutputDst, this.fPublishOutputFolder, stream);
    this.OutputSimpleElement("opttemp", this.fOptOnApplyTemplate, stream);
    this.OutputSimpleElement("showscnum", this.fShowSubChartNum, stream);
  }

  OutputToJSON(json) {
    json["docPreferences"] = {};
    var docPrefs = json["docPreferences"];
    docPrefs["publishDestination"] = {
      destination: this.fPublishOutputDst,
      folder: this.fPublishOutputFolder
    }; //Soft deprecated
    docPrefs["optimizeOnApply"] = this.fOptOnApplyTemplate; //This doesn't make sense as a property in this way.
    docPrefs["showSCNum"] = this.fShowSubChartNum; //This shouldn't live here...

    if (this.fLastSort) docPrefs["sortConfig"] = this.fLastSort.OutputToJSON().sortConfig;

    if (this.fLastOptimize) this.fLastOptimize.OutputToJSON(docPrefs);
  }

  LoadFromJSON(json) {
    if (json["publishDestination"]) {
      this.fPublishOutputDst = json["publishDestination"]["destination"];
      this.fPublishOutputFolder = json["publishDestination"]["folder"];
    }
    this.fOptOnApplyTemplate = json["optimizeOnApply"];
    this.fShowSubChartNum = json["showSCNum"];

    var sortPrefs = json["sortConfig"];
    if (sortPrefs) {
      this.fLastSort = new QSortConfig();
      this.fLastSort.LoadFromJSON(sortPrefs);
    }

    var optimizePrefs = json["optimizePrefs"];
    if (optimizePrefs) {
      this.fLastOptimize = new QOptimizePref();
      this.fLastOptimize.LoadFromJSON(optimizePrefs);
    }
  }

  LoadSubElements(objTree) {
    var valueList = objTree.GetValueListForChild("pubdest", false);
    if (valueList) {
      this.fPublishOutputDst = ConvertNullInt(valueList[0]);
      this.fPublishOutputFolder = ConvertNull(valueList[1]);
    }

    valueList = objTree.GetValueListForChild("opttemp", false);
    var optOnApply = false;
    if (valueList) optOnApply = StringToBool(valueList[0]);
    this.fOptOnApplyTemplate = optOnApply;

    valueList = objTree.GetValueListForChild("showscnum", false);
    var showSCNum = false;
    if (valueList) showSCNum = StringToBool(valueList[0]);
    this.fShowSubChartNum = showSCNum;
  }

  LoadChildElements(objTree) {
    var sortPrefs = objTree.GetChildByName("qsortconfig");
    if (sortPrefs) {
      this.fLastSort = new QSortConfig();
      this.fLastSort.LoadFromXML(sortPrefs);
    }

    var optimizePrefs = objTree.GetChildByName("qoptimizeprefs");
    if (optimizePrefs) {
      this.fLastOptimize = new QOptimizePref();
      this.fLastOptimize.LoadFromXML(optimizePrefs);
    }
  }

  OutputChildElements(stream) {
    if (this.fLastSort) this.fLastSort.OutputToXML(stream);

    if (this.fLastOptimize) this.fLastOptimize.OutputToXML(stream);
  }

  SetupDocPrefs(docPrefs) {
    this.fPublishOutputDst = docPrefs.GetPubDest();
    this.fPublishOutputFolder = docPrefs.GetPubDestFolder();
    this.fOptOnApplyTemplate = docPrefs.GetOptOnApplyTemplate();
    this.fLastOptimize = docPrefs.GetPageOpt(true);
    this.fShowSubChartNum = docPrefs.GetShowSubChartNum();
    this.fLastSort = docPrefs.GetLastSort();
  }

  GetLastSort() {
    if (this.fLastSort == null) return null;

    return this.fLastSort.Clone();
  }

  SetLastSort(lastSort) {
    this.fLastSort = lastSort.Clone();
  }

  GetPageOpt(cloneIt) {
    if (this.fLastOptimize == null) {
      this.fLastOptimize = new QOptimizePref();
      this.fLastOptimize.InitConfig();
    }

    if (cloneIt) return this.fLastOptimize.Clone();
    else return this.fLastOptimize;
  }

  SetPageOpt(lastOptimize) {
    this.fLastOptimize = lastOptimize.Clone();
  }

  Clone() {
    const c = new QDocPrefs();
    c.InitDocPrefs(this.fParentDoc);

    c.fPublishOutputDst = this.fPublishOutputDst;
    c.fPublishOutputFolder = this.fPublishOutputFolder;
    c.fLastSort = this.fLastSort?.Clone();
    c.fLastOptimize = this.fLastOptimize?.Clone();
    c.fOptOnApplyTemplate = this.fOptOnApplyTemplate;
    c.fShowSubChartNum = this.fShowSubChartNum;
    c.fForceShowSubChartNumBM = this.fForceShowSubChartNumBM;

    return c;
  }
}
